import React from "react";
import { MarginProps, WidthProps } from "styled-system";
import { Choice, CommercialUnitDataHour, SecRayonType } from "../../types/commercial";
import BaseDashboardCard from "../BaseDashboardCard";
import Box from "../Box";
import CAByHourPieChart from "../CAByHourPieChart";
import SelectOption from "../SelectOption";
import useBreakPoint from "../../utils/useBreakPoint";
import styled from "styled-components";
import { CText } from "@carrefour/design-system-components-react";
import { SingleValue } from "react-select";
import { useSearchParams } from "react-router-dom";
import { Store } from "../../types/store";
import { eventTrack } from "../../utils/gtag";
import { useTranslation } from "react-i18next";
import { useSectorOptions } from "../../utils/sectorsExtractor";

type CAByHourCardProps = {
  byHourToday: Array<CommercialUnitDataHour>;
  byHourLastYear: Array<CommercialUnitDataHour>;
  title: string;
  subTitle: string;
  storeType: Store["storeType"];
  isUnavailable: boolean;
  isLoading: boolean;
  sectorListData: Array<{ item: Array<SecRayonType> }>;
  importedDateTime?: string;

  openInformationModal: () => void;
} & MarginProps &
  WidthProps;

const Text = styled(CText)`
  font-family: system-ui;
`;
const BoxRectangle = styled(Box)<{ color: string }>`
  /* Serie color */
  width: 16px;
  height: 16px;
  background: ${(props) => props.color};
  border-radius: 4px; /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;
`;
const BoxCustom = styled(Box)<{ gap: string }>`
  gap: ${(props) => props.gap};
  display: flex;
  align-items: center;
`;
const CAByHourCard = ({
  byHourToday,
  byHourLastYear,
  title,
  subTitle,
  storeType,
  openInformationModal,
  sectorListData,
  importedDateTime,
  ...rest
}: CAByHourCardProps) => {
  const {t} = useTranslation();
  const tab = useSectorOptions(sectorListData, "label");

  const isBreakpoint = useBreakPoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const getDefaultValueParams = () => {
    setSearchParams({ byHour: tab?.[0].value as string });
    // first tracking

    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: tab?.[0].value,
    });
    return tab?.[0].value;
  };
  const byHour = searchParams.get("byHour") ?? getDefaultValueParams();

  const handleChange = (choice: SingleValue<Choice>) => {
    const newValue = choice?.value as string;

    if (newValue === byHour) {
      return;
    }
    searchParams.set("byHour", newValue as string);
    setSearchParams(searchParams);
    //tracking the change
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: newValue,
    });
  };

  return (
    <BaseDashboardCard
      title={t(title)}
      subTitle={t(subTitle)}
      size="big"
      hasModal={true}
      openInformationModal={openInformationModal}
      {...rest}
      option={
        <Box width={isBreakpoint ? "100%" : "60%"} py={1}>
          { tab?.length > 1 && (
            <SelectOption 
              list={tab} 
              value={byHour} 
              handleChange={handleChange} 
            />
          )}
        </Box>
      }
      importedDateTime={importedDateTime}
    >
      <CAByHourPieChart
        byHourToday={byHourToday}
        byHourLastYear={byHourLastYear}
      />
      <BoxCustom
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        gap="2rem"
        py={1}
      >
        <BoxCustom gap="8px">
          <BoxRectangle color={"#0970E6"} data-testid={"boxLive"} />
          <Text tagStyle="p">{t("commercial.ca")}</Text>
        </BoxCustom>
        <BoxCustom gap="8px">
          <BoxRectangle color={"#00BF7D"} data-testid={"boxLastWeek"} />
          <Text tagStyle="p">{t("commercial.ca_s_1")}</Text>
        </BoxCustom>
      </BoxCustom>
    </BaseDashboardCard>
  );
};

CAByHourCard.defaultProps = {
  title: "commercial.chiffre_affaires_heure",
  subTitle: "commercial.hors_ecommerce",
  width: "100%",
};

export default CAByHourCard;
